import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useEmployeeDocumentMasterEntryScreen } from './hooks/useEmployeeDocumentMasterEntryScreen';
import { appToast } from '../../../components/AppToast';

function TextField({ label, value, onChange, placeholder, required = false, type = 'text' }) {
  return (
    <label className="grid gap-2">
      <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
        {label}
        {required ? <span className="text-error"> *</span> : null}
      </span>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 py-3.5 text-sm text-on-surface outline-none placeholder:text-on-surface-variant/60 transition-all focus:border-primary focus:ring-1 focus:ring-primary/20 shadow-2xs"
      />
    </label>
  );
}

export default function EmployeeDocumentMasterEntryScreen() {
  const { state, actions } = useEmployeeDocumentMasterEntryScreen();
  const [activeTab, setActiveTab] = useState('editor'); // 'editor' | 'preview'

  useEffect(() => {
    if (state.error) appToast.error(state.error);
  }, [state.error]);

  useEffect(() => {
    if (state.success) appToast.success(state.success);
  }, [state.success]);

  // Mock employee values for live preview
  const getRenderedPreview = (content) => {
    if (!content) return 'No template content entered yet.';
    return content
      .replace(/#EmployeeName#/g, 'Sarah Jenkins')
      .replace(/#JoiningDate#/g, '15-Jan-2024')
      .replace(/#Company#/g, 'Apex Technologies Global Ltd.')
      .replace(/#Branch#/g, 'Headquarters - New York')
      .replace(/#Department#/g, 'Software Engineering')
      .replace(/#Designation#/g, 'Senior Software Engineer')
      .replace(/#Salary#/g, '$95,000 / annum')
      .replace(/#VoucherNo#/g, 'DOC-2024-00892')
      .replace(/#TodayDate#/g, new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }));
  };

  return (
    <div className="mx-auto flex w-full max-w-[1360px] flex-col gap-6 pb-12">
      {/* Top Header */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-outline-variant/10 pb-5">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-on-surface-variant mb-1">
            <span>Masters</span>
            <span className="material-symbols-outlined text-[12px]">chevron_right</span>
            <Link to="/masters/employee-document-master/list" className="hover:text-primary transition-colors">
              Employee Document Master
            </Link>
            <span className="material-symbols-outlined text-[12px]">chevron_right</span>
            <span className="text-primary font-bold">{state.isEditMode ? 'Edit' : 'Create'}</span>
          </div>
          <h1 className="font-display text-4xl font-extrabold tracking-[-0.02em] text-on-surface">
            {state.isEditMode ? 'Edit Document Template' : 'Create Document Template'}
          </h1>
          <p className="mt-1 text-xs text-on-surface-variant font-medium">
            Configure standardized document template text and dynamic merge placeholders for automated employee letters.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/masters/employee-document-master/list"
            className="inline-flex items-center justify-center rounded-xl bg-surface-container-high px-5 py-2.5 text-xs font-bold text-on-surface transition hover:bg-surface-container-highest cursor-pointer"
          >
            Discard
          </Link>
          <button
            type="button"
            onClick={actions.handleSave}
            disabled={state.isSaving}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-6 py-2.5 text-xs font-bold text-on-primary shadow-sm hover:brightness-105 active:scale-[0.98] transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {state.isSaving ? (
              <>
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-on-primary border-t-transparent" />
                <span>Saving...</span>
              </>
            ) : (
              <span>{state.isEditMode ? 'Update Template' : 'Save Template'}</span>
            )}
          </button>
        </div>
      </section>

      {state.isLoading ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-outline-variant/30 bg-surface-container-lowest px-6 py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
          <span className="mt-3 text-xs font-medium text-on-surface-variant">Loading template details...</span>
        </div>
      ) : (
        <section className="grid grid-cols-12 gap-6">
          {/* Main Form (8 Columns) */}
          <div className="col-span-12 space-y-6 lg:col-span-8">
            {/* Card 1: Template Information */}
            <article className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-sm">
              <div className="mb-6 flex items-center gap-3">
                <div className="h-6 w-1 rounded-full bg-primary" />
                <h2 className="text-lg font-bold text-on-surface">Template Details</h2>
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="md:col-span-2">
                  <TextField
                    label="Template Name"
                    required
                    value={state.form.name}
                    onChange={(val) => actions.patchForm('name', val)}
                    placeholder="e.g. Appointment Letter, Confirmation Letter, Relieving Certificate"
                  />
                </div>

                {/* Category Dropdown */}
                <div className="grid gap-2">
                  <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
                    Document Category
                  </span>
                  <div className="relative">
                    <select
                      value={state.form.cat_id}
                      onChange={(e) => actions.patchForm('cat_id', e.target.value)}
                      className="w-full appearance-none rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 py-3.5 pr-10 text-sm text-on-surface outline-none transition focus:border-primary focus:ring-1 focus:ring-primary/20 shadow-2xs cursor-pointer"
                    >
                      <option value="">Select Category (Optional)</option>
                      {state.categories?.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                    <span className="material-symbols-outlined pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-outline text-lg">
                      expand_more
                    </span>
                  </div>
                </div>

                {/* Operational Status */}
                <div className="grid gap-2">
                  <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
                    Operational Status
                  </span>
                  <div className="flex items-center rounded-xl bg-surface-container-low p-1">
                    <button
                      type="button"
                      onClick={() => actions.setOperationalStatus('active')}
                      className={`flex-1 rounded-lg px-3 py-2 text-sm font-bold transition-all cursor-pointer ${
                        state.operationalStatus === 'active'
                          ? 'bg-surface-container-lowest text-primary shadow-sm'
                          : 'text-on-surface-variant'
                      }`}
                    >
                      Active
                    </button>
                    <button
                      type="button"
                      onClick={() => actions.setOperationalStatus('inactive')}
                      className={`flex-1 rounded-lg px-3 py-2 text-sm font-bold transition-all cursor-pointer ${
                        state.operationalStatus === 'inactive'
                          ? 'bg-surface-container-lowest text-on-surface shadow-sm'
                          : 'text-on-surface-variant'
                      }`}
                    >
                      Archived
                    </button>
                  </div>
                </div>
              </div>
            </article>

            {/* Card 2: Interactive Token Palette */}
            <article className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-6 w-1 rounded-full bg-secondary" />
                  <div>
                    <h2 className="text-base font-bold text-on-surface">Available Merge Tokens</h2>
                    <p className="text-xs text-on-surface-variant">
                      Click any token chip below to insert it directly into your template content.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-2">
                {state.tokens?.map((t) => (
                  <button
                    key={t.token}
                    type="button"
                    onClick={() => actions.insertToken(t.token)}
                    title={t.description}
                    className="group inline-flex items-center gap-1.5 rounded-lg border border-primary/20 bg-primary/5 px-3 py-1.5 text-xs font-mono font-semibold text-primary hover:bg-primary hover:text-on-primary transition-all cursor-pointer active:scale-95 shadow-2xs"
                  >
                    <span className="material-symbols-outlined text-[13px] group-hover:rotate-45 transition-transform">
                      add_circle
                    </span>
                    <span>{t.token}</span>
                    <span className="text-[10px] opacity-75 font-sans">({t.label})</span>
                  </button>
                ))}
              </div>
            </article>

            {/* Card 3: Content Editor & Live Preview */}
            <article className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-6 w-1 rounded-full bg-primary" />
                  <h2 className="text-lg font-bold text-on-surface">Document Body & Layout</h2>
                </div>

                {/* View switcher tabs */}
                <div className="flex items-center gap-1 rounded-xl bg-surface-container-low p-1">
                  <button
                    type="button"
                    onClick={() => setActiveTab('editor')}
                    className={`rounded-lg px-3 py-1 text-xs font-bold transition cursor-pointer ${
                      activeTab === 'editor'
                        ? 'bg-surface-container-lowest text-primary shadow-xs'
                        : 'text-on-surface-variant hover:text-on-surface'
                    }`}
                  >
                    Edit Template
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('preview')}
                    className={`rounded-lg px-3 py-1 text-xs font-bold transition cursor-pointer ${
                      activeTab === 'preview'
                        ? 'bg-surface-container-lowest text-primary shadow-xs'
                        : 'text-on-surface-variant hover:text-on-surface'
                    }`}
                  >
                    Live Preview
                  </button>
                </div>
              </div>

              {activeTab === 'editor' ? (
                <div className="space-y-2">
                  <textarea
                    rows={16}
                    value={state.form.content}
                    onChange={(e) => actions.patchForm('content', e.target.value)}
                    placeholder="Dear #EmployeeName#,&#10;&#10;We are pleased to offer you the position of #Designation# at #Company#, #Branch#...&#10;&#10;Your official joining date shall be #JoiningDate#.&#10;&#10;Sincerely,&#10;HR Management"
                    className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low p-4 font-mono text-xs leading-relaxed text-on-surface outline-none placeholder:text-on-surface-variant/50 transition focus:border-primary focus:ring-1 focus:ring-primary/20 shadow-2xs resize-y"
                  />
                  <div className="flex justify-between text-[11px] text-on-surface-variant">
                    <span>Supports plain text or HTML formatting. Merge tokens will be replaced during document generation.</span>
                    <span>{state.form.content?.length || 0} characters</span>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-outline-variant/20 bg-surface-container-lowest p-8 shadow-inner min-h-[360px]">
                  <div className="border-b border-outline-variant/15 pb-4 mb-6 flex items-center justify-between text-xs text-on-surface-variant">
                    <span className="font-bold uppercase tracking-wider text-primary">Preview Output (Sample Data)</span>
                    <span>Document Simulation</span>
                  </div>
                  <div className="whitespace-pre-wrap font-serif text-sm leading-relaxed text-on-surface">
                    {getRenderedPreview(state.form.content)}
                  </div>
                </div>
              )}
            </article>
          </div>

          {/* Sidebar / Aside (4 Columns) */}
          <aside className="col-span-12 space-y-6 lg:col-span-4">
            {/* Completion Card */}
            <article className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-sm">
              <h3 className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
                Configuration Status
              </h3>
              <div className="mt-4 flex items-center justify-between">
                <span className="text-2xl font-black text-on-surface">{state.completionPercent}%</span>
                <span className="text-xs font-bold text-on-surface-variant">Setup Complete</span>
              </div>
              <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-surface-container-high">
                <div
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${state.completionPercent}%` }}
                />
              </div>
            </article>

            {/* Merge Tokens Guide */}
            <article className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-3 text-xs font-bold text-primary">
                <span className="material-symbols-outlined text-base">info</span>
                <span>Token Reference</span>
              </div>
              <p className="text-xs text-on-surface-variant leading-relaxed">
                When generating documents via <strong>Employee Document Generation</strong>, each token enclosed in hash symbols (e.g. <code className="bg-surface-container-high px-1 py-0.5 rounded font-mono text-[11px]">#EmployeeName#</code>) is dynamically substituted with the corresponding employee record from the active tenant database.
              </p>
              <ul className="mt-4 space-y-2 text-xs text-on-surface-variant">
                <li className="flex items-start gap-2">
                  <span className="material-symbols-outlined text-xs text-primary mt-0.5">check_circle</span>
                  <span>Tokens are case-sensitive.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="material-symbols-outlined text-xs text-primary mt-0.5">check_circle</span>
                  <span>Use standard paragraphs and line breaks.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="material-symbols-outlined text-xs text-primary mt-0.5">check_circle</span>
                  <span>Templates can be archived at any time without affecting past generated documents.</span>
                </li>
              </ul>
            </article>
          </aside>
        </section>
      )}
    </div>
  );
}
